import React, { useState } from "react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { EntityEditForm } from "@/components/common/EntityEditForm";
import { useEntityEditForm } from "@/hooks/common/useEntityEditForm";
import { useTranslation } from "react-i18next";
import { JobCustomFieldsEditTab } from "./JobCustomFieldsEditTab";
import { useJobLanguages } from "@/hooks/languages/useJobLanguages";
import { LanguageManager } from "@/components/ui/LanguageManager";
import { JobLanguage } from "@/types/candidate";

interface JobEditTabsProps {
  job: any;
  isEditing: boolean;
  onChange: (field: string, value: string | string[]) => void;
  customFieldsConfig?: any[]; // Custom fields configuration for the organization
}

export const JobEditTabs: React.FC<JobEditTabsProps> = ({ 
  job, 
  isEditing, 
  onChange,
  customFieldsConfig = []
}) => {
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState('basic');

  const { languages, loading: languagesLoading, updateLanguages } = useJobLanguages(job.id);

  const handleFieldChange = (field: string, value: string | string[]) => {
    onChange(field, value);
  };

  const handleLanguagesChange = async (updatedLanguages: any[]) => {
    try {
      const jobLanguages: JobLanguage[] = updatedLanguages.map(lang => ({
        id: lang.id || '',
        job_id: job.id,
        language: lang.language,
        language_code: lang.language_code,
        min_cefr_level: lang.level,
        is_mandatory: lang.is_mandatory || false,
        is_user_owned: lang.is_user_owned,
        created_at: new Date().toISOString()
      }));
      await updateLanguages(jobLanguages);
    } catch (error) {
      console.error("Error updating job languages:", error);
    }
  };

  // Create form configurations for each tab
  const basicFormProps = useEntityEditForm({
    entityType: 'job',
    entity: job,
    onChange: handleFieldChange,
    tabType: 'basic'
  });

  const jobDetailsFormProps = useEntityEditForm({
    entityType: 'job',
    entity: job,
    onChange: handleFieldChange,
    tabType: 'jobDetails'
  });

  const qualificationsFormProps = useEntityEditForm({
    entityType: 'job',
    entity: job,
    onChange: handleFieldChange,
    tabType: 'qualifications'
  });

  const skillsFormProps = useEntityEditForm({
    entityType: 'job',
    entity: job,
    onChange: handleFieldChange,
    tabType: 'skills'
  });

  const additionalInfoFormProps = useEntityEditForm({
    entityType: 'job',
    entity: job,
    onChange: handleFieldChange,
    tabType: 'additionalInfo'
  });

  return (
    <div className="space-y-6">
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="w-full flex flex-wrap gap-1 bg-gray-100 rounded-sm p-1">
          <TabsTrigger value="basic" className="flex-shrink-0 text-xs sm:text-sm">
            {t('jobs.formWizard.basicInfo', 'Basic Info')}
          </TabsTrigger>
          <TabsTrigger value="jobDetails" className="flex-shrink-0 text-xs sm:text-sm">
            {t('jobs.formWizard.jobDetails', 'Job Details')}
          </TabsTrigger>
          <TabsTrigger value="qualifications" className="flex-shrink-0 text-xs sm:text-sm">
            {t('jobs.formWizard.qualifications', 'Qualifications')}
          </TabsTrigger>
          <TabsTrigger value="skills" className="flex-shrink-0 text-xs sm:text-sm">
            {t('jobs.formWizard.skills', 'Skills')}
          </TabsTrigger>
          <TabsTrigger value="additionalInfo" className="flex-shrink-0 text-xs sm:text-sm">
            {t('jobs.formWizard.additionalInfo', 'Additional')}
          </TabsTrigger>
          {customFieldsConfig.length > 0 && (
            <TabsTrigger value="customFields" className="flex-shrink-0 text-xs sm:text-sm">
              {t('jobs.customFields.title', 'Custom')}
            </TabsTrigger>
          )}
        </TabsList>
        
        <TabsContent value="basic" className="mt-6">
          <EntityEditForm
            {...basicFormProps}
            isEditing={isEditing}
            title=""
            showSkillsAtBottom={false}
            entity={job}
          />
        </TabsContent>
        
        <TabsContent value="jobDetails" className="mt-6">
          <EntityEditForm
            {...jobDetailsFormProps}
            isEditing={isEditing}
            title=""
            showSkillsAtBottom={false}
            entity={job}
          />
        </TabsContent>
        
        <TabsContent value="qualifications" className="mt-6">
          <EntityEditForm
            {...qualificationsFormProps}
            isEditing={isEditing}
            title=""
            showSkillsAtBottom={false}
            entity={job}
          />
          
          {/* Languages Section */}
          <div className="mt-6 space-y-2">
            <LanguageManager
              languages={languages.map(lang => ({
                id: lang.id,
                language: lang.language,
                language_code: lang.language_code,
                level: lang.min_cefr_level,
                is_user_owned: lang.is_user_owned,
                is_mandatory: lang.is_mandatory
              }))}
              onChange={handleLanguagesChange}
              type="job"
              disabled={languagesLoading}
            />
          </div>
        </TabsContent>
        
        <TabsContent value="skills" className="mt-6">
          <EntityEditForm
            {...skillsFormProps}
            isEditing={isEditing}
            title=""
            showSkillsAtBottom={false}
            entity={job}
          />
        </TabsContent>
        
        <TabsContent value="additionalInfo" className="mt-6">
          <EntityEditForm
            {...additionalInfoFormProps}
            isEditing={isEditing}
            title=""
            showSkillsAtBottom={false}
            entity={job}
          />
        </TabsContent>
        
        {customFieldsConfig.length > 0 && (
          <TabsContent value="customFields" className="mt-6">
            <JobCustomFieldsEditTab
              job={job}
              onChange={handleFieldChange}
              isEditing={isEditing}
            />
          </TabsContent>
        )}
      </Tabs>
    </div>
  );
}; 